import cn from 'classnames'
import type {FC, ReactNode} from 'react'
import {useMemo, useState} from 'react'
import {Icon} from 'src/common/Icon'
import 'swiper/css'
import type {SwiperClass} from 'swiper/react'
import {Swiper, SwiperSlide} from 'swiper/react'

type BaseEventsCarouselProps<T> = {
  items: T[]
  renderSlide: (item: T, index: number, isActive: boolean) => ReactNode
  getSlideKey: (item: T, index: number) => string | number
  slideClassName?: string
  swiperWrapperClassName?: string
  controlsClassName?: string
  containerClassName?: string
}

const SLIDES_PER_VIEW_BREAKPOINTS = {
  768: {
    slidesPerView: 2,
    spaceBetween: 24,
  },
  1024: {
    slidesPerView: 3,
    spaceBetween: 24,
  },
}

const SLIDES_PER_VIEW = 1
const SPACE_BETWEEN = 24

export const BaseEventsCarousel = <T,>({
  items,
  renderSlide,
  getSlideKey,
  slideClassName,
  swiperWrapperClassName,
  controlsClassName,
  containerClassName,
}: BaseEventsCarouselProps<T>): ReturnType<FC> => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [slidesPerView, setSlidesPerView] = useState<number>(1)

  const handleSwiperInit = (swiper: SwiperClass) => {
    setSwiperInstance(swiper)

    // Get actual slidesPerView from Swiper (handles responsive breakpoints)
    const currentSlidesPerView = typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1

    setSlidesPerView(currentSlidesPerView)
  }

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.activeIndex)

    // Update slidesPerView in case breakpoint changed
    const currentSlidesPerView = typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1

    setSlidesPerView(currentSlidesPerView)
  }

  const maxVisibleIndex = useMemo(() => activeIndex + slidesPerView - 1, [activeIndex, slidesPerView])

  // Calculate total pages based on possible slide positions
  const totalPages = useMemo(() => Math.max(1, items.length - slidesPerView + 1), [items.length, slidesPerView])

  // Current page is simply the active index
  const currentPage = useMemo(() => activeIndex, [activeIndex])

  const pagesArray = Array.from({length: totalPages}, (_, i) => i)

  // Hide controls when there are not enough items to scroll
  const shouldShowControls = items.length > slidesPerView

  return (
    <div className={cn('flex w-full flex-col items-start', containerClassName)}>
      <div className="relative w-full">
        <div className={swiperWrapperClassName}>
          <Swiper
            onActiveIndexChange={handleSlideChange}
            onSwiper={handleSwiperInit}
            slidesPerView={SLIDES_PER_VIEW}
            spaceBetween={SPACE_BETWEEN}
            breakpoints={SLIDES_PER_VIEW_BREAKPOINTS}
            className="w-full !overflow-visible [&_.swiper-wrapper]:!items-stretch"
            centeredSlides={false}
          >
            {items.map((item, index) => {
              const isActive = index >= activeIndex && index <= maxVisibleIndex

              return (
                <SwiperSlide key={getSlideKey(item, index)} className={cn('!h-auto', slideClassName)}>
                  {renderSlide(item, index, isActive)}
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
      </div>

      {/* Custom controls */}
      {shouldShowControls && (
        <div className={cn('flex w-full items-center justify-center gap-12', controlsClassName)}>
          <button
            onClick={() => {
              if (swiperInstance) {
                if (currentPage === 0) {
                  // Loop to last page
                  swiperInstance.slideTo(totalPages - 1)
                } else {
                  swiperInstance.slidePrev()
                }
              }
            }}
            className="flex items-center justify-center"
            aria-label="Previous page"
          >
            <Icon name="arrow" className="h-3 w-[13.595px] rotate-180 text-moss-grey-500" />
          </button>

          <div className="flex items-center gap-2">
            {pagesArray.map((_, pageIndex) => (
              <button
                key={`nav-dot-${pageIndex}`}
                onClick={() => swiperInstance?.slideTo(pageIndex)}
                className={cn('h-2 rounded-[1px] bg-moss-grey-150 transition-all', {
                  'w-2': pageIndex !== currentPage,
                  'w-8 bg-neon-brand-450': pageIndex === currentPage,
                })}
                aria-label={`Go to page ${pageIndex + 1}`}
              />
            ))}
          </div>

          <button
            onClick={() => {
              if (swiperInstance) {
                if (currentPage === totalPages - 1) {
                  // Loop to first page
                  swiperInstance.slideTo(0)
                } else {
                  swiperInstance.slideNext()
                }
              }
            }}
            className="flex items-center justify-center"
            aria-label="Next page"
          >
            <Icon name="arrow" className="h-3 w-[13.595px] text-moss-grey-500" />
          </button>
        </div>
      )}
    </div>
  )
}
